import { useAppSelector } from 'src/store';
import { Controller, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUpdateIntegrationMutation } from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNotification } from 'src/hooks/useNotification';
import { Button } from 'src/components/ui/button';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { EMAIL } from 'src/shared/constants';
import { useDispatch } from 'react-redux';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';

export const FreshchatStep3 = ({
  onClick = (_d?: any) => {},
  onClose = (_v?: any) => {},
}: {
  onClick?: (d?: any) => void;
  onClose?: (v?: any) => void;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const url = selectedIntegration?.integration_data?.webhook_url;
  const email = EMAIL.SUPPORT;
  const loader = useLoader();
  const dispatch = useDispatch();

  const [updateIntegration, { data: integrationData, isLoading: isLoadingUpdateIntegration }] =
    useUpdateIntegrationMutation() as any;

  const { handleSubmit, control } = useForm({
    defaultValues: {
      webhook_api_token: '',
    },
    resolver: yupResolver(
      object({
        webhook_api_token: stringScheme({ required: true }).label('Webhook api token'),
      }),
    ),
  });

  useEffect(() => {
    if (isLoadingUpdateIntegration) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateIntegration]);

  useEffect(() => {
    if (integrationData) {
      dispatch(setSelectedIntegration({ ...selectedIntegration, ...integrationData }));
      onClose(true);
    }
  }, [integrationData]);

  const submit = (data) => {
    const request = {
      status: selectedIntegration.status,
      integration_type: selectedIntegration.integration_type,
      integration_data: {
        ...selectedIntegration.integration_data,
        webhook_api_token: data.webhook_api_token,
      },
    };

    updateIntegration({
      projectId: id,
      integrationId: selectedIntegration.id,
      body: request,
    });
  };

  const copyUrl = async (e) => {
    e.preventDefault();
    await navigator.clipboard.writeText(url);
    notification.success('Copied to clipboard');
  };

  const copyEmail = async (e) => {
    e.preventDefault();
    await navigator.clipboard.writeText(email);
    notification.success('Copied to clipboard');
  };

  return (
    <form
      id="freshchat-step-3-form"
      className={'w-full overflow-hidden flex flex-col'}
      onSubmit={handleSubmit(submit)}
    >
      <div className={'w-full flex flex-col gap-2.5 py-2.5 px-5 bg-light rounded-sm-5'}>
        <p className={'text-sm text-ebony font-semibold'}>Set up Conversation Webhook:</p>
        <ul className={'list-decimal p-2.5'}>
          <li className={'text-sm font-medium text-ebony_opacity_70'}>
            Open Freshchat Admin Settings → Marketplace & Integrations → Conversation Webhooks →
            Click “Enable”.
          </li>
          <li className={'text-sm font-medium text-ebony_opacity_70 mt-2'}>
            <p>Enter the below information in Freshсhat:</p>
            <div className={'flex flex-row items-end gap-1.5'}>
              <div>
                <Input
                  type={'text'}
                  label={'Webhook URL:'}
                  placeholder={'Webhook URL'}
                  value={url}
                  isValid={true}
                  className={
                    '!text-sm !py-2 !px-4 border-[#7A6BE5] hover:!border-[#7A6BE5] !text-[#7A6BE5] !bg-[#7A6BE50F]'
                  }
                  readonly={true}
                />
              </div>
              <Button className={'h-[38px] px-4'} onClick={copyUrl}>
                Copy
              </Button>
            </div>
            <div className={'flex flex-row items-end gap-1.5'}>
              <div>
                <Input
                  type={'text'}
                  label={'Email for failures:'}
                  placeholder={'Email for failures'}
                  value={email}
                  isValid={true}
                  className={
                    '!text-sm !py-2 !px-4 border-[#7A6BE5] hover:!border-[#7A6BE5] !text-[#7A6BE5] !bg-[#7A6BE50F]'
                  }
                  readonly={true}
                />
              </div>
              <Button className={'h-[38px] px-4'} onClick={copyEmail}>
                Copy
              </Button>
            </div>
          </li>
          <li className={'text-sm font-medium text-ebony_opacity_70 mt-2'}>
            Click "Save" in Freshchat — this will generate your Webhook API Key.
          </li>
          <li className={'text-sm font-medium text-ebony_opacity_70 mt-2'}>
            Click “Copy” under Authentication and paste your Webhook API Key below:
            <div className={'mt-4'}>
              <Controller
                control={control}
                name="webhook_api_token"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    placeholder={'Webhook API Key'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    className={'!text-sm !px-4 !py-2'}
                    autoFocus={true}
                  />
                )}
              />
            </div>
          </li>
        </ul>
      </div>
    </form>
  );
};
